let audioContext = null;
let primeListenersAttached = false;
let lastPlayAt = 0;
let htmlAudioSourceUrl = '';
let hasUserInteracted = false;
const activeAudioInstances = new Set();

const PLAY_COOLDOWN_MS = 250;
const SAMPLE_RATE = 22050;
const BEEP_SEQUENCE = [
  { frequency: 659.25, startOffset: 0, duration: 0.12, amplitude: 0.42 },
  { frequency: 987.77, startOffset: 0.12, duration: 0.15, amplitude: 0.5 },
  { frequency: 1318.51, startOffset: 0.28, duration: 0.24, amplitude: 0.62 }
];

const clampSample = (value) => Math.max(-1, Math.min(1, value));

const createWavBlobUrl = () => {
  const totalDurationSeconds = 0.64;
  const totalSamples = Math.floor(SAMPLE_RATE * totalDurationSeconds);
  const pcmData = new Int16Array(totalSamples);

  for (let i = 0; i < totalSamples; i += 1) {
    const time = i / SAMPLE_RATE;
    let sampleValue = 0;

    BEEP_SEQUENCE.forEach(({ frequency, startOffset, duration, amplitude }) => {
      if (time < startOffset || time > startOffset + duration) {
        return;
      }

      const localTime = time - startOffset;
      const attack = Math.min(1, localTime / 0.012);
      const release = Math.min(1, (startOffset + duration - time) / 0.08);
      const envelope = attack * release;
      const fundamental = Math.sin(2 * Math.PI * frequency * localTime);
      const harmonic = Math.sin(2 * Math.PI * frequency * 2 * localTime) * 0.35;
      const shimmer = Math.sin(2 * Math.PI * frequency * 3 * localTime) * 0.12;
      sampleValue += (fundamental + harmonic + shimmer) * amplitude * envelope;
    });

    pcmData[i] = clampSample(sampleValue) * 32767;
  }

  const buffer = new ArrayBuffer(44 + pcmData.length * 2);
  const view = new DataView(buffer);
  const writeString = (offset, value) => {
    for (let i = 0; i < value.length; i += 1) {
      view.setUint8(offset + i, value.charCodeAt(i));
    }
  };

  writeString(0, 'RIFF');
  view.setUint32(4, 36 + pcmData.length * 2, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, SAMPLE_RATE, true);
  view.setUint32(28, SAMPLE_RATE * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeString(36, 'data');
  view.setUint32(40, pcmData.length * 2, true);

  pcmData.forEach((sample, index) => {
    view.setInt16(44 + index * 2, sample, true);
  });

  return URL.createObjectURL(new Blob([buffer], { type: 'audio/wav' }));
};

const getHtmlAudioSourceUrl = () => {
  if (typeof window === 'undefined') {
    return '';
  }

  if (!htmlAudioSourceUrl) {
    htmlAudioSourceUrl = createWavBlobUrl();
  }

  return htmlAudioSourceUrl;
};

const trackAudioInstance = (audio) => {
  if (!audio) {
    return audio;
  }

  activeAudioInstances.add(audio);

  const cleanup = () => {
    audio.onended = null;
    audio.onerror = null;
    activeAudioInstances.delete(audio);
  };

  audio.onended = cleanup;
  audio.onerror = cleanup;
  return audio;
};

const getAudioContext = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextClass) {
    return null;
  }

  if (!audioContext) {
    audioContext = new AudioContextClass();
  }

  return audioContext;
};

const resumeAudioContext = async () => {
  const context = getAudioContext();
  if (!context) {
    return null;
  }

  if (context.state === 'suspended') {
    try {
      await context.resume();
    } catch (error) {
      console.error('Failed to resume audio context:', error);
    }
  }

  return context;
};

const primeHtmlAudio = () => {
  const sourceUrl = getHtmlAudioSourceUrl();
  if (!sourceUrl) {
    return;
  }

  try {
    const audio = trackAudioInstance(new Audio(sourceUrl));
    audio.preload = 'auto';
    audio.muted = true;
    const playPromise = audio.play();

    if (playPromise && typeof playPromise.then === 'function') {
      playPromise
        .then(() => {
          audio.pause();
          audio.currentTime = 0;
        })
        .catch(() => {});
    }
  } catch (error) {
    console.error('Failed to prime HTML audio:', error);
  }
};

const speakFallbackMessage = () => {
  if (typeof window === 'undefined' || !hasUserInteracted || !('speechSynthesis' in window)) {
    return false;
  }

  try {
    const utterance = new SpeechSynthesisUtterance('New order received');
    utterance.volume = 1;
    utterance.rate = 1;
    utterance.pitch = 1;
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utterance);
    return true;
  } catch (error) {
    console.error('Speech synthesis fallback failed:', error);
    return false;
  }
};

export const initializeOrderSound = () => {
  if (typeof window === 'undefined' || primeListenersAttached) {
    return () => {};
  }

  const primeAudio = () => {
    hasUserInteracted = true;
    resumeAudioContext();
    primeHtmlAudio();
  };

  const events = ['pointerdown', 'keydown', 'touchstart'];
  events.forEach((eventName) => {
    window.addEventListener(eventName, primeAudio, { passive: true });
  });

  primeListenersAttached = true;

  return () => {
    events.forEach((eventName) => {
      window.removeEventListener(eventName, primeAudio);
    });
    primeListenersAttached = false;
  };
};

export const playOrderNotificationSound = async () => {
  const now = Date.now();
  if (now - lastPlayAt < PLAY_COOLDOWN_MS) {
    return false;
  }

  lastPlayAt = now;

  let webAudioPlayed = false;

  try {
    const context = await resumeAudioContext();
    if (context && context.state === 'running') {
      const masterGain = context.createGain();
      masterGain.gain.setValueAtTime(0.0001, context.currentTime);
      masterGain.connect(context.destination);

      BEEP_SEQUENCE.forEach(({ frequency, startOffset, duration }) => {
        const primaryOscillator = context.createOscillator();
        const harmonicOscillator = context.createOscillator();
        const gainNode = context.createGain();
        const startAt = context.currentTime + startOffset;
        const endAt = startAt + duration;

        primaryOscillator.type = 'triangle';
        primaryOscillator.frequency.setValueAtTime(frequency, startAt);
        harmonicOscillator.type = 'sine';
        harmonicOscillator.frequency.setValueAtTime(frequency * 2, startAt);

        gainNode.gain.setValueAtTime(0.0001, startAt);
        gainNode.gain.exponentialRampToValueAtTime(0.22, startAt + 0.018);
        gainNode.gain.exponentialRampToValueAtTime(0.0001, endAt);

        primaryOscillator.connect(gainNode);
        harmonicOscillator.connect(gainNode);
        gainNode.connect(masterGain);
        primaryOscillator.start(startAt);
        harmonicOscillator.start(startAt);
        primaryOscillator.stop(endAt + 0.03);
        harmonicOscillator.stop(endAt + 0.03);
      });

      masterGain.gain.exponentialRampToValueAtTime(0.95, context.currentTime + 0.02);
      masterGain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.7);
      webAudioPlayed = true;
    }
  } catch (error) {
    console.error('Web Audio playback failed:', error);
  }

  if (webAudioPlayed) {
    return true;
  }

  if (!hasUserInteracted) {
    return false;
  }

  try {
    const audio = trackAudioInstance(new Audio(getHtmlAudioSourceUrl()));
    audio.preload = 'auto';
    audio.volume = 0.85;
    await audio.play();
    return true;
  } catch (error) {
    console.error('HTML audio playback failed:', error);
    return speakFallbackMessage();
  }
};
